import React, { useEffect, useState } from 'react';
import ReactPivot from 'react-pivot';
import { useCompany, useNominalConfig } from '../../utils/FetchConfig';
import { default as Form } from '../../components/connectedForm';
import Dropdown from '../../components/connectedForm/Dropdown';
import {
  ICompany,
  IFinancialCalendar,
  IFinancialCalendarInput,
  INominalConfigInput,
  INominalTransaction,
  Maybe,
} from '../../graphql/schema/p4types';
import { useFinancialCalendars } from '../../utils/company/useFinancialCalendars';
import { useFlatNominalTransactions } from '../../utils/nominal/useFlatNominalTransactions';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { ProgressBar } from '@fluentui/react-components';

type PivotDimension = {
  value: string;
  title: string;
  sortBy?: (row: any) => any;
  template?: (value: string, row: any) => any;
};
type ICalculations = {
  title: string;
  value: string;
  template: (val: any) => string | number;
  sortBy: (row: any) => number;
  className: string;
};

export type nominalAnalysisProps = {
  nominal?: number;
};

const BASE_PIVOT_DIMENSIONS: Array<PivotDimension> = [
  {
    value: 'documentTypeDisplay',
    title: 'Source Type',
  },
  {
    value: 'financialPeriod.name',
    title: 'Financial Period',
    sortBy: (row) => {
      return row['financialPeriod.periodNumber'];
    },
  },
  {
    value: 'financialPeriod.periodNumber',
    title: 'Financial Period Number',
  },
];

const NominalAnalysis = ({ nominal }: nominalAnalysisProps) => {
  const company: Maybe<ICompany> = useCompany();
  const financialCalendars = useFinancialCalendars(true);

  const [financialYear, setFinancialYear] = useState<Maybe<IFinancialCalendarInput>>(null);

  const nominalConfig: Maybe<INominalConfigInput> = useNominalConfig();
  //NominalTransactions as flattened objects (I.e. {nominal: {id: number}} => {'nominal.id': number}) - Would be a rather complex recursive type so just going with any for the moment.
  const { data: flatNominalTransactions, loading, progress } = useFlatNominalTransactions(nominal, financialYear);

  const [pivotDimensions, setPivotDimensions] = useState<Array<PivotDimension>>(BASE_PIVOT_DIMENSIONS);

  const pivotCalculations: Array<ICalculations> = [
    {
      title: 'Amount',
      value: 'baseAmount',
      template: function (val) {
        return `£${isNaN(val) ? val : val.toFixed(2)}`;
      },
      sortBy: function (row) {
        return isNaN(row.baseAmount) ? 0 : row.baseAmount;
      },
      className: 'pivotAlignRight',
    },
  ];

  useEffect(() => {
    // Nominal has changed, if we don't have a financial year set, then set our initial one now.
    if (!financialYear && company) {
      setFinancialYear(
        normalizeVariables<Maybe<IFinancialCalendar>, Maybe<IFinancialCalendarInput>>(company.currentFinancialYear)
      );
    }
  }, [financialYear, company]);

  useEffect(() => {
    const newPivotDimensions: Array<PivotDimension> = [...BASE_PIVOT_DIMENSIONS];
    if (!nominal) {
      newPivotDimensions.push({
        title: 'Nominal Account',
        value: 'nominal.accountNo',
        template: (value, row) => {
          return `<a href='/Nominal/Accounts/Details/${row['nominal.id']}' target ='_blank'>${value}</a>`;
        },
      });
      newPivotDimensions.push({
        value: 'nominal.name',
        title: 'Account Name',
        template: (value, row) => {
          return `<a href='/Nominal/Accounts/Details/${row['nominal.id']}' target ='_blank'>${value}</a>`;
        },
      });
      newPivotDimensions.push({
        value: 'reportCategory.description',
        title: 'Report Category',
      });
      newPivotDimensions.push({
        value: 'nominal.accountType',
        title: 'Account Type',
        template: (val) => {
          return val === 'BALANCE_SHEET' ? 'Balance Sheet' : 'Profit & Loss';
        },
      });
    }

    if (nominalConfig?.dimension1?.title) {
      newPivotDimensions.push({
        value: 'dimensionValue1.value',
        title: nominalConfig.dimension1.title,
      });
    }

    if (nominalConfig?.dimension2?.title) {
      newPivotDimensions.push({
        value: 'dimensionValue2.value',
        title: nominalConfig.dimension2.title,
      });
    }

    if (nominalConfig?.dimension3?.title) {
      newPivotDimensions.push({
        value: 'dimensionValue3.value',
        title: nominalConfig.dimension3.title,
      });
    }

    setPivotDimensions(newPivotDimensions);
  }, [nominal, nominalConfig]);

  const pivotReduce = (row: any, memo: any) => {
    memo['nominal.id'] = row['nominal.id'];
    memo.baseAmount = (memo.baseAmount || 0) + parseFloat(row.baseAmount);
    memo['financialPeriod.periodNumber'] = parseInt(row['financialPeriod.periodNumber'], 10);
    return memo;
  };

  return (
    <>
      <Form.Row style={{ marginLeft: 0, marginRight: 0 }}>
        <Form.Fieldset cols={1 / 2}>
          <Dropdown
            labelCols={1 / 3}
            inputCols={2 / 3}
            label="Financial Year"
            options={financialCalendars}
            labelKey={'year'}
            value={financialYear}
            onChange={setFinancialYear}
            clearable={false}
          />
        </Form.Fieldset>
      </Form.Row>
      <Form.Row style={{ marginLeft: 0, marginRight: 0 }}>
        {loading ? (
          <div style={{ paddingLeft: '16px', paddingRight: '16px', width: '100%' }}>
            <div>Fetching transactions</div>
            <ProgressBar value={progress} />
          </div>
        ) : (
          <ReactPivot
            rows={flatNominalTransactions}
            dimensions={pivotDimensions}
            reduce={pivotReduce}
            calculations={pivotCalculations}
            compact={true}
            nPaginateRows={10000}
            soloText="Filter"
            activeDimensions={['Financial Period']}
            sortBy={'Financial Period'}
            csvDownloadEnabled={false}
            key={'id'}
          />
        )}
      </Form.Row>
    </>
  );
};
export default NominalAnalysis;
